import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { LogOut, LoaderCircle, Package, Search, Wallet, Bell } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { ThemeToggle, useTheme } from "../contexts/ThemeContext";
import { ChatWidget } from "../components/bite/BiteBot";
import { NotifBell } from "../components/webapp/NotifBell";
import { RoleDashboard } from "../components/webapp/RoleDashboard";
import { MastersAdmin } from "../components/webapp/MastersAdmin";
import { initPush } from "../push";
import {
  FoodPanel,
  LearnPanel,
  CareerPanel,
  ShopPanel,
  TravelPanel,
  GreenPanel,
  CommunityPanel,
} from "../components/webapp/panels";
import { ChefRiderPanel } from "../components/webapp/ChefRider";
import { KitchenHubPanel } from "../components/webapp/KitchenHub";
import { SupplyNetPanel } from "../components/webapp/SupplyNet";
import { ChefGigsPanel } from "../components/webapp/ChefGigs";
import { FundHubPanel } from "../components/webapp/FundHub";
import { HealthBitePanel } from "../components/webapp/HealthBite";
import { AcademyPanel } from "../components/webapp/Academy";
import { ChatCenter } from "../components/webapp/ChatCenter";
import { OrgPanel } from "../components/webapp/OrgAdmin";
import { IdentityPanel } from "../components/webapp/Identity";
import { PhonePanel } from "../components/webapp/PhoneVerify";
import { VendorCompliancePanel } from "../components/webapp/VendorCompliance";
import { PitchRoomPanel } from "../components/webapp/PitchRoom";
import { ScholarshipFinder, BusinessCoach } from "../components/webapp/CoachScholar";
import { JobPortal } from "../components/webapp/JobPortal";
import { StudentLoans, HiringMarketplace, GlobalMarketplace } from "../components/webapp/Marketplace";
import { AIAssistant } from "../components/webapp/AIAssistant";
import { t, useLang, fmtMoney } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_HOME = {
  student: { emoji: "🎓", label: "Student", tag: "Learn, earn & get hired" },
  customer: { emoji: "🛍️", label: "Customer", tag: "Discover everything" },
  chef: { emoji: "👨‍🍳", label: "Student Chef", tag: "Sell your food on campus" },
  "cloud kitchen": { emoji: "🥘", label: "Cloud Kitchen", tag: "Run a delivery kitchen" },
  farmer: { emoji: "🌾", label: "Farmer", tag: "Sell directly to buyers" },
  vendor: { emoji: "🏪", label: "Vendor", tag: "Grow your business" },
  "content creator": { emoji: "🎤", label: "Creator", tag: "Earn from your content" },
  mentor: { emoji: "🧑‍🏫", label: "Mentor", tag: "Teach & guide students" },
  recruiter: { emoji: "🔎", label: "Recruiter", tag: "Find & place candidates" },
  "corporate hr": { emoji: "🏢", label: "Corporate HR", tag: "Hire campus talent" },
  "travel guide": { emoji: "✈️", label: "Travel Partner", tag: "Sell trips & packages" },
  "cafe owner": { emoji: "☕", label: "Cafe Owner", tag: "Build your community" },
  "delivery rider": { emoji: "🛵", label: "Delivery Rider", tag: "Deliver & earn" },
  "school admin": { emoji: "🏫", label: "School Admin", tag: "Run your institution" },
  doctor: { emoji: "🩺", label: "Doctor", tag: "Nutrition consults & diet plans" },
  nutritionist: { emoji: "🥗", label: "Nutritionist", tag: "Custom diet programs" },
};

const RoleHome = ({ openModule }) => {
  const [dash, setDash] = useState(null);
  const load = () => {
    axios.get(`${API}/assistant/dashboard`, { headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` }, withCredentials: true })
      .then((r) => setDash(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const onSwitch = () => load();
    window.addEventListener("funz:role-changed", onSwitch);
    return () => window.removeEventListener("funz:role-changed", onSwitch);
  }, []);
  if (!dash) return null;
  const meta = ROLE_HOME[dash.active_role] || { emoji: "🤖", label: dash.active_role, tag: "" };
  return (
    <section className="glass rounded-3xl border border-[#00D4FF]/20 p-6" data-testid="role-home">
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00D4FF]/20 to-[#7B61FF]/20 text-3xl">{meta.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl font-extrabold" data-testid="role-home-title">{meta.label} mode</p>
          <p className="text-xs text-slate-500">{meta.tag} · your dashboard follows your FUNZI role</p>
        </div>
        <div className="flex gap-2">
          {Object.entries(dash.stats || {}).slice(0, 3).map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-[#09090B]/60 px-4 py-2.5 text-center" data-testid={`role-stat-${k}`}>
              <p className="font-display text-lg font-extrabold">{typeof v === "number" && v > 999 ? `${(v / 1000).toFixed(1)}k` : v}</p>
              <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {(dash.widgets || []).map((w) => (
          <button key={w} data-testid={`role-widget-${w}`} onClick={() => openModule(w)}
            className="rounded-full border border-white/10 px-4 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-wider text-slate-400 transition-colors hover:border-[#00D4FF]/50 hover:text-white">
            {w.replace(/-/g, " ")}
          </button>
        ))}
      </div>
    </section>
  );
};

const SECTIONS = [
  { id: "dashboard", emoji: "📊", label: "Dashboard" },
  { id: "business", emoji: "🏢", label: "Business" },
  { id: "modules", emoji: "🧩", label: "Modules" },
  { id: "feed", emoji: "📰", label: "Feed" },
  { id: "analytics", emoji: "📈", label: "Analytics" },
  { id: "wallet", emoji: "💰", label: "Wallet" },
  { id: "profile", emoji: "👤", label: "Profile" },
  { id: "settings", emoji: "⚙️", label: "Settings" },
];

const MODULES = [
  { id: "food", emoji: "🍔", label: "FoodBite" },
  { id: "learn", emoji: "📚", label: "LearnBite" },
  { id: "career", emoji: "💼", label: "CareerBite" },
  { id: "shop", emoji: "🛍️", label: "ShopBite" },
  { id: "travel", emoji: "✈️", label: "TravelBite" },
  { id: "green", emoji: "🌱", label: "GreenBite" },
  { id: "community", emoji: "🧑‍🤝‍🧑", label: "Communities" },
  { id: "homechef", emoji: "👨‍🍳", label: "ChefRider" },
  { id: "kitchenhub", emoji: "🏭", label: "KitchenHub" },
  { id: "supplynet", emoji: "🥕", label: "SupplyNet" },
  { id: "gigs", emoji: "💼", label: "Chef Gigs" },
  { id: "fundhub", emoji: "🏦", label: "FundHub" },
  { id: "healthbite", emoji: "💚", label: "HealthBite" },
  { id: "academy", emoji: "🎓", label: "Academy" },
  { id: "bot", emoji: "🤖", label: "FUNZI" },
];

// Assistant role → module visibility key, for per-role module ordering
const ASSISTANT_ROLE_MAP = {
  "cloud kitchen": "cloud_kitchen", "content creator": "creator", "corporate hr": "hr",
  "travel guide": "travel_partner", "cafe owner": "cafe_owner", "delivery rider": "rider",
  "school admin": "school_admin", "business consultant": "vendor", "financial advisor": "vendor",
  "software developer": "student", "fitness coach": "student", lawyer: "customer", teacher: "mentor",
  "marketing expert": "creator",
};

// Country-aware branding for the home-chef marketplace module
const HOME_CHEF_LABEL = {
  "United States": "Personal Chef",
  UAE: "Villa Dining",
  "United Kingdom": "Personal Chef",
  Singapore: "Personal Chef",
};

// Role-based module visibility — a student sees a student app, a chef sees a chef platform
const MODULE_VISIBILITY = {
  student: ["learn", "career", "food", "shop", "travel", "green", "community", "homechef", "healthbite", "academy", "fundhub", "bot"],
  customer: ["food", "shop", "travel", "community", "homechef", "healthbite", "academy", "fundhub", "bot"],
  chef: ["food", "shop", "community", "homechef", "kitchenhub", "supplynet", "gigs", "academy", "fundhub", "bot"],
  cloud_kitchen: ["food", "community", "kitchenhub", "supplynet", "homechef", "gigs", "fundhub", "bot"],
  farmer: ["shop", "community", "supplynet", "fundhub", "bot"],
  vendor: ["shop", "community", "supplynet", "kitchenhub", "fundhub", "bot"],
  creator: ["shop", "learn", "community", "fundhub", "bot"],
  mentor: ["learn", "career", "community", "academy", "fundhub", "bot"],
  recruiter: ["career", "community", "fundhub", "bot"],
  hr: ["career", "community", "fundhub", "bot"],
  travel_partner: ["travel", "community", "fundhub", "bot"],
  cafe_owner: ["food", "shop", "community", "homechef", "kitchenhub", "supplynet", "gigs", "fundhub", "bot"],
  rider: ["food", "community", "fundhub", "bot"],
  school_admin: ["learn", "food", "community", "fundhub", "bot"],
  doctor: ["healthbite", "community", "fundhub", "bot"],
  nutritionist: ["healthbite", "community", "fundhub", "bot"],
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const AVATARS = ["😎", "🦊", "🐼", "🦄", "🐸", "🌸", "⚡", "🎧"];

const ProfileCard = () => {
  const { user, setUser } = useAuth();
  const lang = useLang();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar || "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/auth/profile`, { name, avatar }, authHeaders());
      setUser(data);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    setPwBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: curPw, new_password: newPw }, authHeaders());
      toast.success("Password changed.");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <section data-testid="profile-card" className="glass rounded-3xl p-6">
      <h2 className="font-display text-xl font-bold">{t(lang, "your_profile")}</h2>
      <div className="mt-5 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] text-2xl" data-testid="profile-avatar-preview">
          {avatar || (user.name || user.email)[0].toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{user.email}</p>
          <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
            {user.role} · {t(lang, "member_since")} {String(user.created_at).slice(0, 10)}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <p className="mb-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{t(lang, "pick_avatar")}</p>
        <div className="flex flex-wrap gap-2" data-testid="avatar-picker">
          {AVATARS.map((a) => (
            <button
              key={a}
              data-testid={`avatar-${a}`}
              onClick={() => setAvatar(a)}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl transition-all ${
                avatar === a ? "scale-110 border-[#06B6D4] bg-[#06B6D4]/15" : "border-white/10 bg-white/[0.04] hover:border-white/30"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <input
          data-testid="profile-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t(lang, "display_name")}
          className="h-12 flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
        />
        <button
          data-testid="profile-save"
          onClick={saveProfile}
          disabled={busy}
          className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : t(lang, "save")}
        </button>
      </div>
      <div className="mt-6 border-t border-white/10 pt-5">
        <p className="mb-3 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{t(lang, "change_password")}</p>
        <div className="space-y-2.5">
          <input
            data-testid="profile-current-pw"
            type="password"
            value={curPw}
            onChange={(e) => setCurPw(e.target.value)}
            placeholder={t(lang, "current_pw")}
            className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
          />
          <div className="flex gap-2">
            <input
              data-testid="profile-new-pw"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder={t(lang, "new_pw")}
              className="h-12 flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
            />
            <button
              data-testid="profile-pw-save"
              onClick={changePassword}
              disabled={pwBusy || !curPw || newPw.length < 6}
              className="rounded-full border border-white/15 px-5 font-display text-xs font-bold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#06B6D4]/60 hover:text-white disabled:opacity-50"
            >
              {pwBusy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : t(lang, "update")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeedSection = () => {
  const lang = useLang();
  const [notifs, setNotifs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/notifications/mine`, authHeaders()).then((r) => setNotifs(r.data.notifications)).catch(() => setNotifs([]));
  }, []);
  return (
    <section className="glass rounded-3xl p-6" data-testid="feed-section">
      <h2 className="flex items-center gap-2 font-display text-xl font-bold">
        <Bell className="h-5 w-5 text-[#7C3AED]" /> {t(lang, "feed_title")}
      </h2>
      {notifs === null ? (
        <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-[#06B6D4]" />
      ) : notifs.length === 0 ? (
        <p className="mt-5 text-sm text-slate-500">{t(lang, "feed_empty")}</p>
      ) : (
        <div className="mt-5 space-y-3">
          {notifs.map((n) => (
            <div key={n.id} data-testid={`feed-${n.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <p className="text-sm font-bold">{n.title}</p>
              <p className="mt-1 text-xs text-slate-400">{n.body}</p>
              <p className="mt-1.5 font-jbmono text-[10px] text-slate-600">{String(n.created_at).slice(0, 16).replace("T", " ")}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const AnalyticsSection = () => {
  const { user } = useAuth();
  const lang = useLang();
  const [orders, setOrders] = useState([]);
  const [apps, setApps] = useState([]);
  useEffect(() => {
    axios.get(`${API}/orders/mine`, authHeaders()).then((r) => setOrders(r.data.orders)).catch(() => {});
    axios.get(`${API}/applications/mine`, authHeaders()).then((r) => setApps(r.data.applications)).catch(() => {});
  }, []);
  const loc = user.localization || {};
  const spent = orders.reduce((a, o) => a + o.total, 0);
  const stats = [
    { emoji: "🍔", label: t(lang, "stat_orders"), value: orders.length },
    { emoji: "💸", label: t(lang, "stat_spent"), value: fmtMoney(spent, loc.currency || "INR", loc.symbol || "₹") },
    { emoji: "💼", label: t(lang, "stat_apps"), value: apps.length },
    { emoji: "⭐", label: t(lang, "stat_shortlists"), value: apps.filter((a) => a.status === "shortlisted").length },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="analytics-section">
      {stats.map((s) => (
        <div key={s.label} className="glass rounded-2xl p-5">
          <span className="text-2xl">{s.emoji}</span>
          <p className="mt-2.5 font-display text-2xl font-extrabold">{s.value}</p>
          <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

const STEP_KEYS = {
  placed: "tl_placed",
  cooking: "tl_cooking",
  packed: "tl_packed",
  "out-for-delivery": "tl_out",
  shipped: "tl_shipped",
  "rider-assigned": "tl_rider",
  delivered: "tl_delivered",
  cancelled: "tl_cancelled",
};

const FOOD_STEPS = ["placed", "cooking", "out-for-delivery", "delivered"];
const SHOP_STEPS = ["placed", "packed", "shipped", "delivered"];

const OrderTimeline = ({ kind, orderId }) => {
  const lang = useLang();
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    const url = kind === "food" ? `${API}/orders/${orderId}` : `${API}/shop/orders/${orderId}`;
    axios.get(url, authHeaders()).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    const t = setInterval(() => {
      axios.get(url, authHeaders()).then((r) => setDetail(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [kind, orderId]);

  if (!detail) return <LoaderCircle className="my-4 h-5 w-5 animate-spin text-[#06B6D4]" />;

  const steps = kind === "food" ? FOOD_STEPS : SHOP_STEPS;
  const doneStatuses = new Set((detail.status_history || []).map((h) => h.status));
  const timeFor = (s) => (detail.status_history || []).find((h) => h.status === s)?.at;
  const currentIdx = steps.findIndex((s) => s === detail.status);

  if (detail.status === "cancelled") {
    return <p className="mt-3 rounded-xl bg-white/5 px-4 py-3 font-jbmono text-xs text-slate-500">{t(lang, "order_cancelled")}</p>;
  }

  return (
    <div className="mt-4 space-y-0" data-testid={`timeline-${orderId.slice(-6)}`}>
      {steps.map((s, i) => {
        const done = doneStatuses.has(s);
        const isCurrent = i === currentIdx;
        return (
          <div key={s} data-testid={`step-${s}`} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] ${
                done ? "border-[#06B6D4] bg-[#06B6D4]/20 text-[#06B6D4]" : "border-white/15 text-slate-600"
              } ${isCurrent && done ? "animate-pulse" : ""}`}>
                {done ? "✓" : i + 1}
              </span>
              {i < steps.length - 1 && <span className={`h-5 w-px ${done && doneStatuses.has(steps[i + 1]) ? "bg-[#06B6D4]/50" : "bg-white/10"}`} />}
            </div>
            <div className="pb-4">
              <p className={`text-xs font-bold ${done ? "text-white" : "text-slate-600"}`}>{t(lang, STEP_KEYS[s] || s)}</p>
              {timeFor(s) && <p className="font-jbmono text-[10px] text-slate-600">{String(timeFor(s)).slice(0, 16).replace("T", " ")}</p>}
            </div>
          </div>
        );
      })}
      {detail.rider_name && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 rounded-xl bg-[#06B6D4]/[0.06] px-4 py-2.5">
            <p className="font-jbmono text-[11px] text-[#06B6D4]">
              🛵 {detail.rider_name} {t(lang, "rider_handling")}
            </p>
            {detail.rider_id && (
              <button
                data-testid={`chat-rider-${orderId.slice(-6)}`}
                onClick={() => window.dispatchEvent(new CustomEvent("funz:open-chat", { detail: { kind: "order", ref_id: orderId } }))}
                className="rounded-full border border-[#06B6D4]/40 px-3 py-1 font-jbmono text-[9px] font-bold text-[#06B6D4] transition-colors hover:bg-[#06B6D4]/10"
              >
                💬 {t(lang, "tr_chat")}
              </button>
            )}
          </div>
          {detail.rider_location && (
            <div className="overflow-hidden rounded-xl border border-white/10" data-testid={`rider-map-${orderId.slice(-6)}`}>
              <iframe
                title="rider-map"
                width="100%"
                height="160"
                loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${detail.rider_location.lng - 0.01},${detail.rider_location.lat - 0.008},${detail.rider_location.lng + 0.01},${detail.rider_location.lat + 0.008}&layer=mapnik&marker=${detail.rider_location.lat},${detail.rider_location.lng}`}
              />
              <p className="bg-[#09090B] px-3 py-1.5 font-jbmono text-[9px] text-slate-500">
                📍 {t(lang, "tr_live")} · {String(detail.rider_location.at).slice(11, 16)} UTC
              </p>
            </div>
          )}
        </div>
      )}
      {detail.pod_url && (
        <div className="mt-2" data-testid={`pod-${orderId.slice(-6)}`}>
          <p className="mb-1 font-jbmono text-[9px] uppercase tracking-widest text-slate-500">📸 {t(lang, "tr_pod")}</p>
          <img src={`${process.env.REACT_APP_BACKEND_URL}${detail.pod_url}`} alt="proof of delivery" className="max-h-36 rounded-xl border border-white/10" />
        </div>
      )}
      <p className="mt-1 font-jbmono text-[9px] uppercase tracking-widest text-slate-600">{t(lang, "live_refresh")}</p>
    </div>
  );
};

const WalletSection = () => {
  const { user } = useAuth();
  const lang = useLang();
  const [orders, setOrders] = useState(null);
  const [shopOrders, setShopOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  useEffect(() => {
    axios.get(`${API}/orders/mine`, authHeaders()).then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    axios.get(`${API}/shop/orders/mine`, authHeaders()).then((r) => setShopOrders(r.data.orders)).catch(() => {});
  }, []);
  const loc = user.localization || {};
  const all = [
    ...(orders || []).map((o) => ({ ...o, kind: "food" })),
    ...shopOrders.map((o) => ({ ...o, kind: "shop" })),
  ].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  return (
    <div className="space-y-4" data-testid="wallet-section">
      <section className="rounded-3xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/15 to-[#06B6D4]/10 p-6 backdrop-blur-xl">
        <p className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-400">
          <Wallet className="h-4 w-4" /> {t(lang, "bitecoins")}
          <span className="rounded-full bg-[#22C55E]/10 px-2 py-0.5 text-[8px] font-bold text-[#22C55E]">{t(lang, "sample")}</span>
        </p>
        <p className="mt-3 font-display text-4xl font-black">{fmtMoney(2480, loc.currency || "INR", loc.symbol || "₹")}</p>
        <p className="mt-1 text-xs text-slate-400">{t(lang, "wallet_hint")}</p>
      </section>
      <section className="glass rounded-3xl p-6">
        <h3 className="font-display text-lg font-bold">{t(lang, "orders_tracking")}</h3>
        <p className="mt-0.5 text-xs text-slate-500">{t(lang, "orders_tracking_hint")}</p>
        {orders === null ? (
          <LoaderCircle className="mt-4 h-5 w-5 animate-spin text-[#06B6D4]" />
        ) : all.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">{t(lang, "no_orders")}</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {all.slice(0, 10).map((o) => (
              <div key={o.id} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-3.5">
                <button
                  data-testid={`wallet-order-${o.id.slice(-6)}`}
                  onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  className="flex w-full items-center justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">
                      {o.kind === "food" ? "🍔" : "🛍️"} #{o.id.slice(-6)} · {o.status}
                    </p>
                  </div>
                  <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">{fmtMoney(o.total, loc.currency || "INR", loc.symbol || "₹")}</span>
                </button>
                {expanded === o.id && <OrderTimeline kind={o.kind} orderId={o.id} />}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const SettingsSection = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const lang = useLang();
  const [verifySent, setVerifySent] = useState(false);
  const resendVerify = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { origin: window.location.origin }, authHeaders());
      setVerifySent(true);
      toast.success("Verification email sent — check your inbox.");
    } catch {
      toast.error("Could not send the email right now.");
    }
  };
  return (
    <div className="space-y-4" data-testid="settings-section">
      {user.email_verified === false && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[#F97316]/30 bg-[#F97316]/[0.06] p-6 backdrop-blur-xl" data-testid="verify-card">
          <div>
            <p className="font-display text-lg font-bold">Verify your email</p>
            <p className="mt-0.5 text-xs text-slate-500">Order updates, gig alerts and payout emails need a confirmed address.</p>
          </div>
          {verifySent ? (
            <span className="rounded-full bg-[#22C55E]/10 px-5 py-2.5 font-jbmono text-[10px] font-bold text-[#22C55E]" data-testid="verify-sent">Link sent — valid 24h</span>
          ) : (
            <button data-testid="verify-resend" onClick={resendVerify}
              className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B]">
              Send verification email
            </button>
          )}
        </section>
      )}
      <section className="glass flex items-center justify-between rounded-3xl p-6">
        <div>
          <p className="font-display text-lg font-bold">{t(lang, "appearance")}</p>
          <p className="mt-0.5 text-xs text-slate-500">{theme} · {t(lang, "theme_hint")}</p>
        </div>
        <ThemeToggle />
      </section>
      <section className="glass flex flex-wrap items-center justify-between gap-3 rounded-3xl p-6">
        <div>
          <p className="font-display text-lg font-bold">{t(lang, "your_role")}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            <span className="font-bold text-[#06B6D4]">{user.role}</span> · {t(lang, "role_hint")}
          </p>
        </div>
        <Link
          to="/onboarding"
          data-testid="switch-role"
          className="rounded-full border border-[#7C3AED]/40 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#A78BFA] transition-colors hover:bg-[#7C3AED]/10"
        >
          {t(lang, "switch_role")}
        </Link>
      </section>
    </div>
  );
};

export default function WebApp() {
  const { user, checked, logout } = useAuth();
  const [section, setSection] = useState("dashboard");
  const [module, setModule] = useState(null);
  const [query, setQuery] = useState("");
  const [assistantRole, setAssistantRole] = useState("");

  useEffect(() => {
    document.title = "My FUNZ — Web App";
  }, []);

  useEffect(() => {
    if (user) {
      initPush();
      localStorage.setItem("funz_lang", user.localization?.language || "en");
    }
    document.documentElement.dir = user?.localization?.language === "ar" ? "rtl" : "ltr";
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/assistant/state`, authHeaders()).then((r) => setAssistantRole(r.data.active_role || "")).catch(() => {});
    const onSwitch = (e) => setAssistantRole(e.detail || "");
    window.addEventListener("funz:role-changed", onSwitch);
    return () => window.removeEventListener("funz:role-changed", onSwitch);
  }, []);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;  if (user.role === "user" && !user.onboarded) return <Navigate to="/onboarding" replace />;

  const lang = user.localization?.language || "en";
  const rtl = lang === "ar";
  const country = user.localization?.country || "";

  const ALL_SECTIONS = [
    ...SECTIONS,
    ...(user.role === "admin" ? [{ id: "masters", emoji: "🗄️", label: "Masters" }] : []),
  ].map((s) => ({ ...s, label: t(lang, s.id) === s.id ? s.label : t(lang, s.id) }));

  const MODULES_L = MODULES.map((m) =>
    m.id === "homechef" ? { ...m, label: HOME_CHEF_LABEL[country] || m.label } : m
  );

  const openModule = (m) => {
    setModule(m);
    setSection("modules");
  };

  const PANELS = {
    food: <FoodPanel />,
    learn: <LearnPanel />,
    career: <CareerPanel />,
    shop: <ShopPanel />,
    travel: <TravelPanel />,
    green: <GreenPanel />,
    community: <CommunityPanel />,
    homechef: <ChefRiderPanel />,
    kitchenhub: <KitchenHubPanel />,
    supplynet: <SupplyNetPanel />,
    gigs: <ChefGigsPanel />,
    fundhub: <FundHubPanel />,
    healthbite: <HealthBitePanel />,
    academy: <AcademyPanel />,
    bot: <ChatWidget />,
  };

  const allowedModules = user.role === "admin" ? MODULES_L.map((m) => m.id) : MODULE_VISIBILITY[user.role] || MODULE_VISIBILITY.customer;
  // Per-role ordering: the active FUNZI role's modules surface first
  const roleOrder = MODULE_VISIBILITY[ASSISTANT_ROLE_MAP[assistantRole] || assistantRole.replace(/ /g, "_")] || [];
  const visibleModules = MODULES_L.filter(
    (m) => allowedModules.includes(m.id) && m.label.toLowerCase().includes(query.trim().toLowerCase())
  ).sort((a, b) => {
    const ai = roleOrder.indexOf(a.id), bi = roleOrder.indexOf(b.id);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
  const activeModule = module && allowedModules.includes(module) ? module : null;

  const SECTION_BODY = {
    dashboard: (
      <div className="space-y-6">
        <RoleHome openModule={openModule} />
        <AIAssistant />
        <RoleDashboard user={user} openModule={openModule} />
      </div>
    ),
    business: (
      <div className="space-y-6">
        <IdentityPanel />
        <PhonePanel />
        <OrgPanel />
        <BusinessCoach />
        <VendorCompliancePanel />
        <PitchRoomPanel />
        <JobPortal />
        <HiringMarketplace />
        <StudentLoans />
        <GlobalMarketplace />
        <ScholarshipFinder />
      </div>
    ),
    modules: (
      <div data-testid="modules-section" className="space-y-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {visibleModules.map((m) => (
            <button
              key={m.id}
              data-testid={`module-${m.id}`}
              onClick={() => setModule(m.id)}
              className={`group rounded-2xl border p-4 text-left backdrop-blur-xl transition-all hover:-translate-y-1 ${
                activeModule === m.id ? "border-[#06B6D4]/60 bg-[#06B6D4]/[0.06]" : "border-white/10 bg-white/[0.04] hover:border-[#7C3AED]/50"
              }`}
            >
              <span className="inline-block text-3xl transition-transform duration-300 group-hover:scale-125">{m.emoji}</span>
              <p className="mt-2.5 text-sm font-bold">{m.label}</p>
              <p className="mt-0.5 font-jbmono text-[10px] text-[#06B6D4]">{activeModule === m.id ? t(lang, "open_down") : t(lang, "open_arrow")}</p>
            </button>
          ))}
        </div>
        {activeModule && <div data-testid={`module-panel-${activeModule}`}>{PANELS[activeModule]}</div>}
        {!module && <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">{t(lang, "modules_hint")}</p>}
      </div>
    ),
    feed: <FeedSection />,
    analytics: <AnalyticsSection />,
    wallet: <WalletSection />,
    profile: <ProfileCard />,
    settings: <SettingsSection />,
    masters: user.role === "admin" ? <MastersAdmin /> : null,
  };

  return (
    <div data-testid="webapp" dir={rtl ? "rtl" : "ltr"} className="grid-bg min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)]">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[var(--bg-base)]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="webapp-logo" className="flex shrink-0 items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">
              FUN<span className="text-[#06B6D4]">Z</span>
            </span>
          </Link>
          <div className="relative hidden flex-1 sm:block">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 rtl:left-auto rtl:right-4" />
            <input
              data-testid="header-search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSection("modules");
              }}
              placeholder={t(lang, "search")}
              className="w-full max-w-xs rounded-full border border-white/10 bg-white/[0.04] py-2 pl-11 pr-4 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60 rtl:pl-4 rtl:pr-11"
            />
          </div>
          <div className="ml-auto flex items-center gap-2.5 rtl:ml-0 rtl:mr-auto">
            <button
              data-testid="header-funzi"
              onClick={() => openModule("bot")}
              className="flex h-9 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 font-display text-xs font-bold text-white transition-transform hover:scale-105"
            >
              🤖 FUNZI
            </button>
            <ThemeToggle />
            <NotifBell />
            <span className="hidden h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] font-display text-sm font-black text-white sm:flex" data-testid="webapp-avatar">
              {user.avatar || (user.name || user.email)[0].toUpperCase()}
            </span>
            <Link to="/rules" data-testid="rules-link" className="rounded-full border border-white/10 px-3 py-2 font-jbmono text-[10px] font-bold text-slate-400 transition-colors hover:border-[#A78BFA]/60 hover:text-white">
              Rules
            </Link>
            <button
              data-testid="webapp-logout"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-[#F97316]/60 hover:text-[#F97316]"
            >
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t(lang, "logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6 sm:px-6">
        <aside className="glass sticky top-[86px] hidden h-fit w-48 shrink-0 flex-col gap-1.5 rounded-3xl p-3 lg:flex" data-testid="sidebar">
          {ALL_SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`section-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-2.5 rounded-2xl px-4 py-3 font-display text-xs font-bold transition-colors ${
                section === s.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span>{s.emoji}</span> {s.label}
            </button>
          ))}
        </aside>

        <main className="min-w-0 flex-1 pb-20 lg:pb-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="webapp-greeting">
            {section === "dashboard"
              ? `${t(lang, "greeting")} ${(user.name?.split(" ") || []).find((w) => w && w.toLowerCase() !== "funz") || "hustler"} 👋`
              : ALL_SECTIONS.find((s) => s.id === section)?.label}
            {user.localization?.country && (
              <span className="ml-3 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-jbmono text-[10px] text-slate-400" data-testid="locale-chip">
                {user.localization.flag} {user.localization.country} · {user.localization.currency} · {user.localization.timezone}
              </span>
            )}
          </h1>
          {section === "dashboard" && (
            <p className="mt-1 font-jbmono text-[10px] uppercase tracking-[0.3em] text-slate-600" data-testid="tagline">{t(lang, "tagline")}</p>
          )}
          <div className="mt-6">{SECTION_BODY[section]}</div>
        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#09090B]/90 backdrop-blur-2xl lg:hidden" data-testid="mobile-nav">
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto px-3 py-2.5">
          {ALL_SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`m-section-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 font-display text-xs font-bold transition-colors ${
                section === s.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "text-slate-400"
              }`}
            >
              <span>{s.emoji}</span> {s.label}
            </button>
          ))}
        </div>
      </nav>
      <Toaster theme="dark" position="top-center" />
      <ChatCenter />
    </div>
  );
}
